<template>
  <div class="page-container">
    <!-- 头部 -->
    <div class="page-header">
      <div>
        <h2 class="page-title">{{ pageTitle }}</h2>
      </div>
      <div class="page-header-extra">
        <span>共 <b>{{ total }}</b> 条</span>
        <span class="divider">|</span>
        <span>未读 <b>{{ unreadCount }}</b></span>
      </div>
    </div>

    <!-- 筛选 + 操作 -->
    <el-card class="search-card" shadow="never">
      <div class="search-bar">
        <el-form :inline="true" :model="query" size="small" class="search-form">
          <el-form-item v-if="isBusiness" label="类型">
            <el-select v-model="query.type" placeholder="全部" clearable style="width: 130px">
              <el-option v-for="t in MESSAGE_TYPES" :key="t.value" :label="t.label" :value="t.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="query.read" placeholder="全部" clearable style="width: 120px">
              <el-option label="未读" :value="0" />
              <el-option label="已读" :value="1" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
        <div class="action-bar">
          <el-button
            type="primary" icon="el-icon-finished" size="small"
            :disabled="unreadCount === 0"
            @click="markAllRead"
          >全部标为已读</el-button>
        </div>
      </div>
    </el-card>

    <!-- 列表 -->
    <el-card class="table-card" shadow="never">
      <el-table v-loading="loading" :data="list" border size="small" height="100%" row-key="id">
        <el-table-column label="内容" min-width="420">
          <template slot-scope="scope">
            <div class="cell-title" :class="{ 'cell-unread': canMark(scope.row) }" :title="scope.row.title">
              <span v-if="canMark(scope.row)" class="unread-dot" />
              {{ scope.row.title }}
            </div>
            <div class="cell-sub" :title="scope.row.content">{{ scope.row.content }}</div>
          </template>
        </el-table-column>
        <el-table-column v-if="isBusiness" label="类型" width="110" align="center">
          <template slot-scope="scope">
            <el-tag :type="messageTypeTagType(scope.row.type)" size="mini" effect="plain">
              {{ messageTypeText(scope.row.type) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="时间" width="170" align="center" />
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="scope">
            <el-tag :type="readTagType(scope.row)" size="mini" effect="plain">{{ readText(scope.row) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="110" align="center">
          <template slot-scope="scope">
            <el-button
              v-if="canMark(scope.row)"
              type="text" size="mini"
              :loading="readingId === scope.row.id"
              @click="markRead(scope.row)"
            >标为已读</el-button>
            <span v-else class="cell-muted">—</span>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrap">
        <el-pagination
          :current-page="page"
          :page-size="pageSize"
          :total="total"
          layout="total, prev, pager, next"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>
  </div>
</template>

<script>
// 顶部消息中心接收端页面：铃铛=我的通知（type=6 后台公告）/ 信封=我的消息（type 1-5 业务提醒）
// 同一张表 tb_admin_message 的两种入口，共用本组件，路由 props.mode 区分，接口用 scene 区分。
// 全体公告（admin_id=0）一行被所有管理员共享，逐人已读在后端按人记录（Mongo 集合 mg_admin_message_read），
// 列表返回统一后的 can_read / is_read：都可标已读，本人没读过的全体公告也计入角标；公告发布/删除有 SSE 推送，角标即时变。
// 业务消息点进去看详情（按 biz_type/biz_id 跳业务页）暂不做。
import { getMessageList, readMessage, readAllMessage } from '@/api/mine'
import {
  MESSAGE_TYPES,
  messageTypeText, messageTypeTagType, readText, readTagType
} from '@/mock/message'

export default {
  name: 'MineMessages',
  props: {
    // notice = 我的通知（铃铛）；business = 我的消息（信封）
    mode: {
      type: String,
      default: 'notice'
    }
  },
  data() {
    return {
      loading: false,
      readingId: 0,
      page: 1,
      pageSize: 10,
      total: 0,
      unreadCount: 0,
      list: [],
      // 输入中的筛选条件，点查询才提交到 applied
      query: { type: '', read: '' },
      applied: { type: '', read: '' },

      MESSAGE_TYPES,
      messageTypeText,
      messageTypeTagType,
      readText,
      readTagType
    }
  },
  computed: {
    isBusiness() { return this.mode === 'business' },
    // 接口场景：通知 = 后台公告；消息 = 业务提醒
    scene() { return this.isBusiness ? 'message' : 'notice' },
    pageTitle() { return this.isBusiness ? '我的消息' : '我的通知' },
    pageDesc() {
      return this.isBusiness
        ? '预约、核销、举报等业务提醒，处理后可在对应业务页面跟进'
        : '管理员公告，由后台「公告管理」发布'
    },
    // 实时通道推送的时间戳（store 里维护，见 store/index.js 的 connectMessageStream）
    noticeEventAt() { return this.$store.getters.getNoticeEventAt }
  },
  watch: {
    // 后台刚发了新公告：停在本页时立刻重拉，回到第一页看最新一条
    noticeEventAt() {
      if (this.isBusiness) return
      this.page = 1
      this.loadList()
    }
  },
  created() {
    this.loadList()
  },
  methods: {
    // 可标记已读：未读的（点对点与全体公告后端都已统一 is_read 口径）
    canMark(row) {
      return Number(row.can_read) === 1 && Number(row.is_read) !== 1
    },
    loadList() {
      this.loading = true
      getMessageList({
        scene: this.scene,
        type: this.applied.type === '' || this.applied.type === null ? '' : this.applied.type,
        read: this.applied.read === '' || this.applied.read === null ? '' : this.applied.read,
        page: this.page,
        page_size: this.pageSize
      }).then(res => {
        if (res.code !== 200) return
        const data = res.data || {}
        this.list = data.list || []
        this.total = Number(data.total) || 0
        this.unreadCount = Number(data.unread_count) || 0
      }).catch(() => {}).finally(() => {
        this.loading = false
      })
    },
    handleSearch() {
      this.applied = { type: this.query.type, read: this.query.read }
      this.page = 1
      this.loadList()
    },
    resetSearch() {
      this.query = { type: '', read: '' }
      this.applied = { type: '', read: '' }
      this.page = 1
      this.loadList()
    },
    handlePageChange(p) {
      this.page = p
      this.loadList()
    },
    markRead(row) {
      this.readingId = row.id
      readMessage(row.id).then(res => {
        if (res.code !== 200) return
        this.$message.success('已标为已读')
        this.loadList()
        // 顶栏角标跟着刷新
        this.$store.dispatch('loadUnreadCount')
      }).catch(() => {}).finally(() => {
        this.readingId = 0
      })
    },
    markAllRead() {
      if (this.unreadCount === 0) return
      this.$confirm(`确认把未读的${this.pageTitle}全部标为已读？`, '批量已读', { type: 'warning' })
        .then(() => readAllMessage({ scene: this.scene }))
        .then(res => {
          if (!res || res.code !== 200) return
          const updated = res.data && res.data.updated ? res.data.updated : 0
          this.$message.success(`已标为已读${updated ? '（' + updated + ' 条）' : ''}`)
          this.page = 1
          this.loadList()
          this.$store.dispatch('loadUnreadCount')
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.cell-title {
  font-weight: 500;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.cell-unread {
  color: var(--primary);
}
.unread-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: var(--primary);
  margin-right: 6px;
  vertical-align: 2px;
}
.cell-sub {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.cell-muted {
  color: #c0c4cc;
}
</style>
